import { t } from "i18next";
import { Button, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useState, useMemo } from "react";
import { Athlete, AthletePerformance, emptyAthlete } from "../../../../interfaces/athlete";
import { createAthletePerformance, deleteAthletePerformance, getAthletePerformance, getAthleteSingle } from "../../../../services/athlete";
import { Media } from "../../../../interfaces/media";
import DataTable from "react-data-table-component";
import moment from "moment";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { tableStyles, getPaginationOptions } from "../../../../util/data-table";
import { useKpiSettings } from "../../../../contexts/KpiSettingsContext";
import { timeToSeconds, formatKpiValue } from "../../../../util/time-format";

interface CoachAthletePerformanceProps {
    id: number;
    onSave: CallableFunction;
    onClose: CallableFunction;
}

export function CoachAthletePerformance(props: CoachAthletePerformanceProps) {
    const { getKpiLabelWithUnit, getActiveKpis, kpiSettingsLoaded } = useKpiSettings();
    const [athlete, setAthlete] = useState<Athlete>(emptyAthlete);
    const [media, setMedia] = useState<Media[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [athletePerformances, setAthletePerformances] = useState<AthletePerformance[]>([]);
    const [showAdd, setShowAdd] = useState(false);

    useEffect(() => {
        Promise.all([
            getAthleteSingle(props.id),
            getAthletePerformance(props.id)
        ]).then((response: any) => {
            setAthlete(response[0].data);
            const newMedia = response[0].data.avatar.map((m: Media) => {
                return m;
            });
            setMedia(newMedia);
            setAthletePerformances(response[1].data);
            setLoaded(true);
        });
    }, []);

    const handleSave = (event: any) => {
        event.preventDefault();

        // Build dynamic KPIs object from form data
        const activeKpis = getActiveKpis();
        const kpis: Record<string, number> = {};

        activeKpis.forEach(kpi => {
            const value = event.target[kpi.kpi_name]?.value;
            if (value !== '' && value !== null && value !== undefined) {
                // Convert time format to seconds if KPI is time type
                if (kpi.data_type === 'time') {
                    kpis[kpi.kpi_name] = timeToSeconds(value);
                } else {
                    kpis[kpi.kpi_name] = parseFloat(value);
                }
            }
        });

        const body = {
            athlete_id: athlete.id,
            performance_timestamp: event.target.performance_date.value + ' ' + (event.target.performance_time?.value || '00:00:00'),
            kpis: kpis
        };

        createAthletePerformance(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    const newAthletePerformances = [
                        ...response.data.data,
                        ...athletePerformances
                    ];
                    setAthletePerformances(newAthletePerformances);
                    setShowAdd(false);
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t("Error saving performance"));
            });
    }
    
    const handleClose = () => {
        props.onClose();
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        if (!window.confirm(t("Are you sure you want to delete this performance record?"))) {
            return;
        }

        const timestamp = event.currentTarget.dataset.timestamp;
        const athleteId = event.currentTarget.dataset.athleteid;

        // Delete by athlete_id and performance_timestamp
        deleteAthletePerformance(`0?athlete_id=${athleteId}&performance_timestamp=${encodeURIComponent(timestamp)}`)
            .then((response: any) => {
                // Remove the deleted record from the list
                const newAthletePerformances = athletePerformances.filter(
                    (ap: AthletePerformance) => ap.performance_timestamp !== timestamp
                );
                setAthletePerformances(newAthletePerformances);
                alert(t("Performance deleted successfully"));
            })
            .catch((error: any) => {
                console.error(error);
                alert(t("Error deleting performance"));
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAdd(true);
    }

    const handleCloseAddNew = (event: any) => {
        event.preventDefault();
        setShowAdd(false);
    }


    const CustomActions = (row: any) => (
        <span>
            <a
                href="#"
                className="icon-link delete"
                title={ t("Delete") }
                onClick={ handleDelete }
                data-timestamp={ row.row.performance_timestamp }
                data-athleteid={ row.row.athlete_id }
            >
                <i className="bi bi-trash"></i>
            </a>
        </span>
    );

    const CustomDate = (row: any ) => (
        <span>
            { moment(row.row.performance_date).format("DD/MM/YYYY") }
        </span>
    );

    // Usar useMemo para que las columnas se actualicen cuando cambien los KPI settings
    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: 'performance_timestamp',
                name: t('performance_date'),
                selector: (row: any) => row.performance_timestamp,
                sortable: true,
                cell: (row: any) => <CustomDate row={row} />,
                width: '150px'
            }
        ];

        // Add dynamic KPI columns
        const activeKpis = getActiveKpis();
        const kpiColumns = activeKpis.map(kpi => ({
            id: kpi.kpi_name,
            name: getKpiLabelWithUnit(kpi.kpi_name),
            selector: (row: any) => row.kpis?.[kpi.kpi_name] ?? null,
            cell: (row: any) => {
                const value = row.kpis?.[kpi.kpi_name];
                if (value === null || value === undefined) return '-';
                return formatKpiValue(value, kpi.data_type);
            },
            sortable: true,
            center: true,
            width: '150px'
        }));

        const actionsColumn = {
            id: 'actions',
            name: t("actions"),
            cell: (row: any) => <CustomActions row={row} />,
            width: '100px'
        };

        return [...baseColumns, ...kpiColumns, actionsColumn];
    }, [kpiSettingsLoaded, getKpiLabelWithUnit, getActiveKpis]);

    return (
        <Modal className="admin-athlete-performance-container" show={ true }
            onHide={ handleClose } size="xl"> 
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("athlete_performances") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { !loaded && 
                    <LoadingSpinner />
                }
                { loaded &&
                    <div className="athlete-performances-container">
                        <div className="subtable-container">
                            <DataTable
                                columns={ columns }
                                data={athletePerformances}
                                // selectableRows={ true }
                                // selectableRowsHighlight={ true }
                                responsive={ true }
                                striped={ true }
                                highlightOnHover={ true }
                                defaultSortFieldId={ 'performance_date' }
                                defaultSortAsc={ false }
                                pagination={ true }
                                customStyles={ tableStyles }
                                paginationComponentOptions={ getPaginationOptions() }
                            />
                        </div>
                        { !showAdd &&
                            <a className="performance-add-new" href="#" onClick={ handleAddNew }>
                                <i className="bi bi-plus-circle"></i>
                            </a>
                        }
                        { showAdd &&
                            <Form onSubmit={ handleSave } className="add-performance">
                                <a href="#" className="close" onClick={ handleCloseAddNew }>
                                </a>
                                <div className="fields">
                                    <Form.Control type="hidden" name="id" required defaultValue={ athlete.id } />
                                    <Form.Group className="mb-3" controlId="performance_date">
                                        <Form.Label aria-required="true">{ t("performance_date") }</Form.Label>
                                        <Form.Control type="date" name="performance_date" required />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="performance_time">
                                        <Form.Label>{ t("Time") }</Form.Label>
                                        <Form.Control type="time" name="performance_time" defaultValue="00:00" />
                                    </Form.Group>

                                    {/* Dynamic KPI Fields */}
                                    {getActiveKpis().map((kpi, index) => (
                                        <Form.Group className="mb-3" controlId={kpi.kpi_name} key={kpi.kpi_name}>
                                            <Form.Label>
                                                { getKpiLabelWithUnit(kpi.kpi_name) }
                                            </Form.Label>
                                            {kpi.data_type === 'time' ? (
                                                <Form.Control
                                                    type="text"
                                                    name={kpi.kpi_name}
                                                    placeholder={t("e.g., 45.494 or 01:45.494 or 01:23:45.494")}
                                                    title={t("Accepted formats: ss.ms, mm:ss.ms or hh:mm:ss.ms")}
                                                />
                                            ) : (
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    name={kpi.kpi_name}
                                                    placeholder={kpi.description || '0.00'}
                                                />
                                            )}
                                            {kpi.description && (
                                                <Form.Text className="text-muted">
                                                    {kpi.description}
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    ))}
                                </div>
                                <div className="actions">
                                    <Button type="submit" variant="primary">
                                        { t("Save") }
                                    </Button>
                                </div>
                            </Form>
                        }
                    </div>
                }
            </Modal.Body>
        </Modal>
    );
}